/*
  BITCO looping carousel (homepage best sellers).

  Markup: [data-bitco-carousel] > [data-carousel-viewport] > [data-carousel-track] > slides,
  plus an optional [data-carousel-controls] group with prev / next / play-pause buttons.

  The loop is endless without cloning slides: moving forward slides the track one card left,
  then moves the first card to the end and resets the offset in the same frame; moving back
  does the reverse. Cloned cards would duplicate product links, wishlist buttons and app
  price hooks, which is why the DOM is rotated instead.

  Autoplay pauses on hover, keyboard focus and touch, when the tab is hidden, and when the
  carousel is off screen. It never starts for prefers-reduced-motion; swipe, drag and the
  buttons keep working there, just without the slide animation.
*/
(function () {
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function Carousel(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-carousel-viewport]');
    this.track = root.querySelector('[data-carousel-track]');
    this.controls = root.querySelector('[data-carousel-controls]');
    this.toggleBtn = root.querySelector('[data-carousel-toggle]');
    this.interval = parseFloat(root.getAttribute('data-interval')) || 4500;
    this.autoplayWanted = root.getAttribute('data-autoplay') === 'true';
    this.userPaused = false;
    this.pauseReasons = {};
    this.busy = false;
    this.timer = null;
    this.drag = null;
    this.suppressClick = false;

    if (!this.track || this.track.children.length < 2) return;

    this.enabled = false;
    this.bind();
    this.refresh();
  }

  // A row that already shows every card has nothing to scroll to; sliding it would only
  // expose an empty strip. Carousel mode (clipping, controls, autoplay) is on only while the
  // cards overflow, re-checked on resize.
  Carousel.prototype.refresh = function () {
    var wasEnabled = this.enabled;
    this.root.classList.remove('is-carousel');
    this.setX(0, false);
    var overflow = this.track.scrollWidth > this.viewport.clientWidth + 1;
    this.enabled = overflow;
    this.root.classList.toggle('is-carousel', overflow);
    if (this.controls) this.controls.hidden = !overflow;
    this.viewport.scrollLeft = 0;
    if (overflow !== wasEnabled || overflow) this.schedule();
  };

  Carousel.prototype.step = function () {
    var first = this.track.firstElementChild;
    if (!first) return 0;
    var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  };

  Carousel.prototype.animated = function () {
    return !(reduceMotion && reduceMotion.matches);
  };

  Carousel.prototype.setX = function (x, animate) {
    this.track.style.transition = animate ? '' : 'none';
    this.track.style.transform = x ? 'translate3d(' + x + 'px,0,0)' : '';
  };

  // Run `done` once the track's transform transition ends (or at once when not animating).
  Carousel.prototype.after = function (animate, done) {
    var self = this;
    if (!animate) { done(); return; }
    var finished = false;
    function finish() {
      if (finished) return;
      finished = true;
      self.track.removeEventListener('transitionend', onEnd);
      clearTimeout(fallback);
      done();
    }
    function onEnd(e) { if (e.target === self.track) finish(); }
    this.track.addEventListener('transitionend', onEnd);
    var fallback = setTimeout(finish, 900);
  };

  // Keep keyboard focus on the element that had it: moving its slide in the DOM would
  // otherwise drop focus to <body>.
  Carousel.prototype.keepFocus = function (fn) {
    var active = document.activeElement;
    var inside = active && this.track.contains(active);
    fn();
    if (inside && document.activeElement !== active) {
      try { active.focus({ preventScroll: true }); } catch (e) { active.focus(); }
    }
  };

  Carousel.prototype.next = function () {
    if (this.busy || !this.enabled) return;
    var self = this;
    var animate = this.animated();
    this.busy = true;
    this.setX(-this.step(), animate);
    this.after(animate, function () {
      self.setX(0, false);
      self.keepFocus(function () { self.track.appendChild(self.track.firstElementChild); });
      void self.track.offsetWidth;
      self.busy = false;
    });
  };

  Carousel.prototype.prev = function () {
    if (this.busy || !this.enabled) return;
    var self = this;
    var animate = this.animated();
    this.busy = true;
    this.keepFocus(function () {
      self.track.insertBefore(self.track.lastElementChild, self.track.firstElementChild);
    });
    this.setX(-this.step(), false);
    void this.track.offsetWidth;
    this.setX(0, animate);
    this.after(animate, function () { self.busy = false; });
  };

  // Tabbing onto a card that is cut off or out of view rotates it to the front.
  Carousel.prototype.revealFocused = function (target) {
    if (!this.enabled || this.busy || this.drag) return;
    var slide = target && target.closest && target.closest('[data-carousel-track] > *');
    if (!slide || slide.parentNode !== this.track) return;
    var v = this.viewport.getBoundingClientRect();
    var r = slide.getBoundingClientRect();
    if (r.left >= v.left - 1 && r.right <= v.right + 1) return;
    var self = this;
    this.setX(0, false);
    this.keepFocus(function () {
      while (self.track.firstElementChild !== slide) self.track.appendChild(self.track.firstElementChild);
    });
    this.viewport.scrollLeft = 0;
  };

  /* ---- autoplay ---------------------------------------------------------------------- */

  Carousel.prototype.canAutoplay = function () {
    if (!this.enabled || !this.autoplayWanted || this.userPaused) return false;
    if (reduceMotion && reduceMotion.matches) return false;
    for (var k in this.pauseReasons) { if (this.pauseReasons[k]) return false; }
    return true;
  };

  Carousel.prototype.schedule = function () {
    var self = this;
    clearTimeout(this.timer);
    this.timer = null;
    this.syncToggle();
    if (!this.canAutoplay()) return;
    this.timer = setTimeout(function () {
      self.next();
      self.schedule();
    }, this.interval);
  };

  Carousel.prototype.pause = function (reason, on) {
    this.pauseReasons[reason] = on;
    this.schedule();
  };

  Carousel.prototype.syncToggle = function () {
    if (!this.toggleBtn) return;
    var reduced = reduceMotion && reduceMotion.matches;
    this.toggleBtn.hidden = !this.autoplayWanted || reduced;
    var playing = !this.userPaused;
    this.toggleBtn.classList.toggle('is-paused', !playing);
    this.toggleBtn.setAttribute(
      'aria-label',
      this.toggleBtn.getAttribute(playing ? 'data-label-pause' : 'data-label-play') || ''
    );
  };

  /* ---- drag / swipe ------------------------------------------------------------------ */

  Carousel.prototype.onPointerDown = function (e) {
    if (e.button !== undefined && e.button !== 0) return;
    if (this.busy || !this.enabled) return;
    this.pause('touch', true);
    this.drag = { x: e.clientX, y: e.clientY, dx: 0, id: e.pointerId, active: false, base: 0 };
  };

  Carousel.prototype.onPointerMove = function (e) {
    var d = this.drag;
    if (!d || e.pointerId !== d.id) return;
    var dx = e.clientX - d.x;
    var dy = e.clientY - d.y;
    if (!d.active) {
      if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
      d.active = true;
      this.root.classList.add('is-dragging');
      try { this.viewport.setPointerCapture(e.pointerId); } catch (err) {}
      // Put the last card in front so a drag to the right has something to reveal.
      this.track.insertBefore(this.track.lastElementChild, this.track.firstElementChild);
      d.stepPx = this.step();
      d.base = -d.stepPx;
    }
    d.dx = dx;
    this.setX(d.base + dx, false);
  };

  Carousel.prototype.onPointerUp = function (e) {
    var d = this.drag;
    if (!d || (e && e.pointerId !== d.id)) return;
    this.drag = null;
    var self = this;
    this.root.classList.remove('is-dragging');
    if (!d.active) {
      this.releaseTouchPause();
      return;
    }
    this.suppressClick = true;
    setTimeout(function () { self.suppressClick = false; }, 0);

    var threshold = Math.min(80, d.stepPx * 0.18);
    var animate = this.animated();
    this.busy = true;
    if (d.dx < -threshold) {
      // Forward: show the card after the original first one.
      this.setX(d.base - d.stepPx, animate);
      this.after(animate, function () {
        self.setX(0, false);
        self.keepFocus(function () {
          self.track.appendChild(self.track.firstElementChild);
          self.track.appendChild(self.track.firstElementChild);
        });
        self.busy = false;
      });
    } else if (d.dx > threshold) {
      // Back: the card moved to the front is the new first one.
      this.setX(0, animate);
      this.after(animate, function () { self.busy = false; });
    } else {
      this.setX(d.base, animate);
      this.after(animate, function () {
        self.setX(0, false);
        self.keepFocus(function () { self.track.appendChild(self.track.firstElementChild); });
        self.busy = false;
      });
    }
    this.releaseTouchPause();
  };

  // Touch has no "mouse leave", so give the shopper a moment before autoplay resumes.
  Carousel.prototype.releaseTouchPause = function () {
    var self = this;
    clearTimeout(this.touchTimer);
    this.touchTimer = setTimeout(function () { self.pause('touch', false); }, 6000);
  };

  Carousel.prototype.bind = function () {
    var self = this;
    var prev = this.root.querySelector('[data-carousel-prev]');
    var next = this.root.querySelector('[data-carousel-next]');
    if (prev) prev.addEventListener('click', function () { self.prev(); self.schedule(); });
    if (next) next.addEventListener('click', function () { self.next(); self.schedule(); });
    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', function () {
        self.userPaused = !self.userPaused;
        self.schedule();
      });
    }

    // Phones fire a synthetic mouseenter on tap and no mouseleave until the next tap
    // elsewhere, so hover pausing listens to real mice only; touch has its own timed pause.
    this.root.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') self.pause('hover', true); });
    this.root.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') self.pause('hover', false); });
    // Pause for keyboard focus only: a mouse click on a control focuses it too, and that
    // must not stop autoplay (the Resume button would otherwise do nothing).
    this.root.addEventListener('focusin', function (e) {
      var keyboard = true;
      try { keyboard = e.target.matches(':focus-visible'); } catch (err) {}
      if (!keyboard) return;
      self.pause('focus', true);
      self.revealFocused(e.target);
    });
    this.root.addEventListener('focusout', function (e) {
      if (!self.root.contains(e.relatedTarget)) self.pause('focus', false);
    });
    // The viewport clips with overflow:hidden, but focusing an off-screen card still scrolls
    // it. Keep scrollLeft at 0; revealFocused() brings the card in by rotating instead.
    this.viewport.addEventListener('scroll', function () {
      if (self.enabled && self.viewport.scrollLeft !== 0) self.viewport.scrollLeft = 0;
    });

    this.viewport.addEventListener('pointerdown', function (e) { self.onPointerDown(e); });
    this.viewport.addEventListener('pointermove', function (e) { self.onPointerMove(e); });
    this.viewport.addEventListener('pointerup', function (e) { self.onPointerUp(e); });
    this.viewport.addEventListener('pointercancel', function (e) { self.onPointerUp(e); });
    this.viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });
    // A drag that ends over a card must not also open the product.
    this.viewport.addEventListener('click', function (e) {
      if (self.suppressClick) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    this.root.addEventListener('keydown', function (e) {
      if (e.target.closest && e.target.closest('input, textarea, select')) return;
      if (e.key === 'ArrowLeft') { self.prev(); self.schedule(); }
      if (e.key === 'ArrowRight') { self.next(); self.schedule(); }
    });

    document.addEventListener('visibilitychange', function () { self.pause('hidden', document.hidden); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        self.pause('offscreen', !entries[0].isIntersecting);
      }, { threshold: 0.25 }).observe(this.root);
    }
    if (reduceMotion && reduceMotion.addEventListener) {
      reduceMotion.addEventListener('change', function () { self.schedule(); });
    }
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { if (!self.busy && !self.drag) self.refresh(); }, 150);
    });
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-bitco-carousel]').forEach(function (root) {
      if (root.bitcoCarousel) return;
      root.bitcoCarousel = new Carousel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
